import { useEffect, useRef } from 'react';
import { getUserKycStatus } from '../services/kycApi';
import type { UserKycStatusResponse } from '../services/kycApi';

// ─────────────────────────────────────────────────────────────────────────────
// Constantes del polling de reanudación
// ─────────────────────────────────────────────────────────────────────────────

// Intervalo entre sondeos (5 segundos)
const RESUME_POLL_INTERVAL_MS = 5_000;

// Número máximo de sondeos antes de rendirse (120 × 5s = 10 minutos).
// Si el pipeline no resuelve en ese tiempo, la pantalla expone el botón "dar por terminado"
// y el flujo cae al fail-safe: IDLE → CAPTURING_ID.
const MAX_POLL_ATTEMPTS = 120;

interface UseResumePollOptions {
  userId: string;
  token: string;
  // Solo activo cuando el status del reducer es RESUMING_SESSION.
  // Cuando es false, el hook no hace nada (sin llamadas, sin timers).
  enabled: boolean;
  // Llamado cuando el sondeo detecta que el proceso ya no está esperando el pipeline:
  //   - status distinto de 'in_progress' (pipeline terminó), o
  //   - in_progress + flowStep="awaiting_poa" (pipeline del pasaporte completó, pendiente POA).
  onStatusChange: (response: UserKycStatusResponse) => void;
  // Llamado cuando se alcanza MAX_POLL_ATTEMPTS sin cambio de estado.
  onTimeout: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook principal
// ─────────────────────────────────────────────────────────────────────────────
//
// Sondea getUserKycStatus cada RESUME_POLL_INTERVAL_MS mientras el proceso
// del usuario esté en estado 'in_progress'. En cuanto cambia, llama a
// onStatusChange para que el controlador despacha la transición de estado.
//
// Si el estado permanece 'in_progress' más de MAX_POLL_ATTEMPTS sondeos,
// llama a onTimeout para que el flujo pueda recuperarse.
//
export function useResumePolling({
  userId,
  token,
  enabled,
  onStatusChange,
  onTimeout,
}: UseResumePollOptions): void {
  const onStatusChangeRef = useRef(onStatusChange);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  });

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  });

  useEffect(() => {
    if (!enabled || !userId) return;

    let attempts = 0;
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    async function poll(): Promise<void> {
      if (cancelled) return;
      attempts += 1;

      try {
        const response = await getUserKycStatus(userId, token);
        if (cancelled) return;

        // Salir del loop de espera en dos casos:
        //   1. El pipeline terminó (status ya no es in_progress).
        //   2. El pipeline del pasaporte completó pero el usuario aún no ha subido el
        //      comprobante (in_progress + flowStep="awaiting_poa"). El status no cambia
        //      porque el proceso sigue activo, pero el usuario debe tomar acción.
        if (response.status !== 'in_progress' || response.flowStep === 'awaiting_poa') {
          onStatusChangeRef.current(response);
          return;
        }
      } catch {
        // Error de red — no es crítico; continuar sondeando para no bloquear al usuario
      }

      if (attempts >= MAX_POLL_ATTEMPTS) {
        // Se agotaron los intentos sin cambio de estado
        onTimeoutRef.current();
        return;
      }

      // Programar el siguiente sondeo si aún no se canceló
      timerId = setTimeout(() => {
        void poll();
      }, RESUME_POLL_INTERVAL_MS);
    }

    // Primer sondeo con un pequeño delay inicial para evitar doble-llamada inmediata
    // (CHECKING_STATUS ya consultó el estado justo antes de entrar a RESUMING_SESSION)
    timerId = setTimeout(() => {
      void poll();
    }, RESUME_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timerId !== null) {
        clearTimeout(timerId);
      }
    };
    // Solo se activa/desactiva con enabled; userId y token son estables durante el flujo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userId, token]);
}
